import React, { useCallback } from "react";
import { useFocusable } from "@noriginmedia/norigin-spatial-navigation";
import { useNavigate } from "react-router-dom";
import { ArrowLeftIcon } from "../legacy-ui/icons";
import { useInputMode } from "../../utils/hooks/useInputMode";

/**
 * ViewMoreCard - A focusable "View More" card for content row sliders.
 * 
 * Placed at the end of content rows when items > 12.
 * Styled with non-blur gradient glassmorphism effect for legacy TV compatibility.
 * 
 * @param {Object} props
 * @param {Function} props.onFocus - Called when card receives focus
 * @param {string} props.actionLink - Navigation link in format "type;target"
 * @param {Function} props.onMoreTap - Optional callback override for tap action
 * @param {number} props.width - Card width (default 250)
 * @param {number} props.height - Card height (default 350)
 * @param {string} props.shapeType - Shape type for sizing (movie, music, etc.)
 */

var ViewMoreCard = function (props) {
  var onFocus = props.onFocus;
  var onBlur = props.onBlur;
  var actionLink = props.actionLink;
  var onMoreTap = props.onMoreTap;
  var width = props.width || 250;
  var height = props.height || 350;
  var shapeType = props.shapeType || "movie";
  var contentKind = props.contentKind;
  var focusKey = props.focusKey;
  var onArrowPress = props.onArrowPress;

  var navigate = useNavigate();

  // Parse action link format: "type;target" or "type;target;extra"
  var parseActionLink = function (link) {
    if (!link) return null;
    var parts = link.split(";");
    if (parts.length < 2) return null;

    var type = parts[0];
    var target = parts[1];

    if (!type || !target) return null;

    switch (type) {
      case "genre":
        return "/genre/" + target;
      case "category":
        return "/category/" + target;
      case "artist":
      case "artists":
        return "/artists/" + target;
      case "movie":
      case "series":
      case "program":
      case "music":
      case "animation":
        return "/" + type + "/" + target;
      default:
        return null;
    }
  };

  var handleEnterPress = function () {
    if (onMoreTap) {
      onMoreTap();
    } else {
      var route = parseActionLink(actionLink);
      if (route) {
        // Append content type filter so the view more page locks to this kind
        var finalRoute = contentKind ? route + "?kind=" + contentKind : route;
        navigate(finalRoute);
      }
    }
  };

  var focusableResult = useFocusable({
    onFocus: onFocus,
    onBlur: onBlur,
    onEnterPress: handleEnterPress,
    onArrowPress: onArrowPress,
    focusKey: focusKey || "view_more_card",
  });

  var focused = focusableResult.focused;
  var ref = focusableResult.ref;
  var focusSelf = focusableResult.focusSelf;

  var inputModeResult = useInputMode();
  var setMouseFocus = inputModeResult.setMouseFocus;
  var handleMouseEnter = useCallback(function () {
    setMouseFocus(true);
    focusSelf();
  }, [focusSelf, setMouseFocus]);

  // Dimensions matching content card aspect ratios
  var getDimensions = function () {
    switch (shapeType) {
      case "music":
      case "animation":
        return { width: 250, height: 250 };
      case "artist":
        return { width: 215, height: 215 };
      case "program":
      case "sport":
        return { width: 620, height: 350 };
      case "movie":
      case "series":
      case "searchbox":
      default:
        return { width: width, height: height };
    }
  };

  var dimensions = getDimensions();
  var cardWidth = dimensions.width;
  var cardHeight = dimensions.height;

  return (
    <div
      ref={ref}
      onMouseEnter={handleMouseEnter}
      onClick={handleEnterPress}
      className={"legacy-view-more-card" + (focused ? " is-focused" : "")}
      style={{
        width: cardWidth + "px",
        minWidth: cardWidth + "px",
        height: cardHeight + "px",
        minHeight: cardHeight + "px",
      }}
    >
      <div className="legacy-view-more-content">
        <div className="legacy-view-more-inner">
          <div className="legacy-view-more-icon-circle">
            <div className="legacy-view-more-icon-inner">
              <ArrowLeftIcon
                style={{
                  color: focused ? "#FFFFFF" : "rgba(255, 255, 255, 0.8)",
                  fontSize: "28px",
                }}
              />
            </div>
          </div>
          <div className="legacy-view-more-label">مشاهده همه</div>
        </div>
      </div>
      <div className="legacy-view-more-border" />
    </div>
  );
};

export default ViewMoreCard;
